import {
  AiSuggestion,
  AskResponse,
  AiProviderTestResult,
  AiProviderView,
  AiRoute,
  AiSettingsView,
  AiStatus,
  AiUsage,
  EventExplanationView,
  type AiProviderInput,
  type AiRoutingRequest,
  type AiSuggestionListQuery,
  type ApproveSuggestionInput,
  type AskInput,
  type AiSettingsInput,
} from '@bartizo/shared';
import { z } from 'zod';
import { ApiError, apiRequest } from './client.js';

/** The AI layer (knowledge doc sections 13 and 15; P9.2). */

export const getAiStatus = (): Promise<AiStatus> => apiRequest('/ai/status', { schema: AiStatus });

export const getAiSettings = (): Promise<AiSettingsView> =>
  apiRequest('/ai/settings', { schema: AiSettingsView });

export const saveAiSettings = (body: AiSettingsInput): Promise<AiSettingsView> =>
  apiRequest('/ai/settings', { method: 'PUT', body, schema: AiSettingsView });

export const listAiProviders = (): Promise<AiProviderView[]> =>
  apiRequest('/ai/providers', { schema: z.array(AiProviderView) });

export const createAiProvider = (body: AiProviderInput): Promise<AiProviderView> =>
  apiRequest('/ai/providers', { method: 'POST', body, schema: AiProviderView });

export const updateAiProvider = (id: string, body: AiProviderInput): Promise<AiProviderView> =>
  apiRequest(`/ai/providers/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    schema: AiProviderView,
  });

export const deleteAiProvider = (id: string): Promise<AiProviderView> =>
  apiRequest(`/ai/providers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: AiProviderView,
  });

export const testAiProvider = (id: string, model: string): Promise<AiProviderTestResult> =>
  apiRequest(`/ai/providers/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    body: { model },
    schema: AiProviderTestResult,
  });

export const listAiRoutes = (): Promise<AiRoute[]> =>
  apiRequest('/ai/routing', { schema: z.array(AiRoute) });

export const saveAiRoutes = (body: AiRoutingRequest): Promise<AiRoute[]> =>
  apiRequest('/ai/routing', { method: 'PUT', body, schema: z.array(AiRoute) });

export const getAiUsage = (): Promise<AiUsage> => apiRequest('/ai/usage', { schema: AiUsage });

/** The stored explanation, or `null` when the event has none yet. */
export async function getExplanation(eventId: string): Promise<EventExplanationView | null> {
  try {
    return await apiRequest(`/events/${encodeURIComponent(eventId)}/explanation`, {
      schema: EventExplanationView,
    });
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 404) return null;
    throw caught;
  }
}

export const explainEvent = (eventId: string): Promise<EventExplanationView> =>
  apiRequest(`/events/${encodeURIComponent(eventId)}/explanation`, {
    method: 'POST',
    schema: EventExplanationView,
  });

export const listSuggestions = (status: AiSuggestionListQuery['status']): Promise<AiSuggestion[]> =>
  apiRequest(`/ai/suggestions${status === undefined ? '' : `?status=${status}`}`, {
    schema: z.array(AiSuggestion),
  });

export const approveSuggestion = (
  id: string,
  body: ApproveSuggestionInput,
): Promise<{ suggestion: AiSuggestion; actionId: string }> =>
  apiRequest(`/ai/suggestions/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body,
    schema: z.object({ suggestion: AiSuggestion, actionId: z.uuid() }),
  });

export const rejectSuggestion = (id: string, note: string): Promise<AiSuggestion> =>
  apiRequest(`/ai/suggestions/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: note.trim() === '' ? {} : { note: note.trim() },
    schema: AiSuggestion,
  });

export const suggestForEvent = (eventId: string): Promise<AiSuggestion> =>
  apiRequest(`/events/${encodeURIComponent(eventId)}/suggestion`, {
    method: 'POST',
    schema: AiSuggestion,
  });

export const askAi = (body: AskInput): Promise<AskResponse> =>
  apiRequest('/ai/ask', { method: 'POST', body, schema: AskResponse });
